import React, { useRef, useState, useEffect } from 'react'
import Downshift, { StateChangeOptions } from 'downshift'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import { useHeaderAutoSuggest, SearchOptions } from './useHeaderAutoSuggest'
import HeaderSearchInput from './HeaderSearchInput'
import HeaderSearchMenu from './HeaderSearchMenu'
import { useCurrentUser, SitePreference } from '@src/utils/useCurrentUser'
import { Suggestion, CartRequestAction } from '@src/types/graphql-types'
import {
  SearchFocusType,
  useSearchOptions,
  SearchOption,
  getValidSearchQuery,
} from '@src/utils/searchUtils'
import { useMediaQuery } from '@material-ui/core'
import { useHeaderSearchAddToCart } from './useHeaderSearchAddToCart'
import { useRouter, CartType } from '@src/routes'
import { useFullCartRefetch } from '@src/utils/useFullCartRefetch'
import { getCartMiniAction } from '@src/utils/cartUtils'
import MarketplaceSellerModal from '@src/components/MarketplaceSellerModal'

const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
    position: 'relative',
  },
}))

const HeaderSearch: React.FC<{ id: string }> = (props) => {
  const theme = useTheme()
  const route = useRouter()
  const validSearchQuery = getValidSearchQuery({
    focus: route.query.focus,
  })
  const [isDropDownOpen, setIsDropDownOpen] = useState<boolean>(false)
  const isDesktop = useMediaQuery(theme.breakpoints.up('md')) === true
  const [focusType, setFocusType] = useState<SearchFocusType>(
    validSearchQuery.focus || SearchFocusType.Products
  )
  const { getSitePreference } = useCurrentUser()
  const orgId = getSitePreference(SitePreference.CatalogFilter)
  const classes = useStyles()
  const inputEl = useRef<HTMLInputElement>(null)
  const {
    handleSearch,
    searchTerm,
    setSearchTerm,
    allProductNumberSuggestions,
    suggestions,
    suggestionsCount,
    recentSearches,
    recentPurchases,
    minTermLength,
    getItemIndex,
  } = useHeaderAutoSuggest(inputEl, isDropDownOpen)

  const handleDropdownChange = (changes: StateChangeOptions<SearchOptions>) => {
    if (changes.isOpen != null) {
      setIsDropDownOpen(changes.isOpen)
    }
  }

  const [prodNumberQuickLook, setProdNumberQuickLook] =
    useState<Suggestion | null>(null)
  const prodNumberQuickLookRef = useRef(prodNumberQuickLook)
  const [shouldDisplayProductQuickLook, setShouldDisplayProductQuickLook] =
    useState(true)

  useEffect(() => {
    prodNumberQuickLookRef.current = null
    setShouldDisplayProductQuickLook(true)
  }, [searchTerm])

  useEffect(() => {
    const match = allProductNumberSuggestions?.find((item: Suggestion) => {
      // Match should be be "equivalent" based on English but case-insensitive
      const compare = item.label.localeCompare(searchTerm, 'en', {
        sensitivity: 'base',
      })
      return compare === 0
    })
    if (match) {
      setProdNumberQuickLook(match)
      if (match.productKey !== prodNumberQuickLookRef.current?.productKey) {
        prodNumberQuickLookRef.current = match
      }
    }
  }, [searchTerm, allProductNumberSuggestions])

  const searchOptions: SearchOption[] = useSearchOptions()

  useEffect(() => {
    // Sets search bars searchType to products on mobile regardless of current focus
    // Also if focus is not an option will default to products
    const isOption = searchOptions.find(
      (item: SearchOption) => item.value === validSearchQuery.focus
    )
    if (isDesktop && isOption) {
      return setFocusType(validSearchQuery.focus)
    }
    return setFocusType(SearchFocusType.Products)
  }, [validSearchQuery.focus, isDesktop, searchOptions])

  const router = useRouter()
  const { refetchFullCart } = useFullCartRefetch()
  const headerSearchAddToCartProps = useHeaderSearchAddToCart(
    searchTerm,
    focusType,
    prodNumberQuickLook
  )

  const [marketplaceModalIsOpen, setMarketplaceModalIsOpen] = useState(false)
  const toggleMarketplaceSellerModalOpen = () => {
    setMarketplaceModalIsOpen((open) => !open)
  }

  return (
    <Downshift<SearchOptions>
      id={`header-search-${props.id}`}
      itemToString={() => searchTerm}
      inputValue={searchTerm}
      onInputValueChange={(newSearch) => setSearchTerm(newSearch)}
      onChange={(item) => {
        if (item) {
          handleSearch({ ...item, focusType })
        }
      }}
      onStateChange={(changes) => handleDropdownChange(changes)}
    >
      {({
        getInputProps,
        getMenuProps,
        getItemProps,
        isOpen,
        highlightedIndex,
        closeMenu,
        openMenu,
        clearSelection,
        inputValue,
      }) => {
        const _headerSearchAddToCartProps: typeof headerSearchAddToCartProps = {
          ...headerSearchAddToCartProps,
          material: shouldDisplayProductQuickLook
            ? headerSearchAddToCartProps.material
            : null,
          onSuccess: (returnCartType: CartType | null | undefined) => {
            const isCartPage = router.pathname === '/[country]/[language]/cart'
            if (isCartPage) {
              const cartType = router?.query?.type as CartType | undefined
              //refetch banner
              if (
                cartType === CartType.Active &&
                returnCartType &&
                returnCartType === CartType.BlueCart
              ) {
                refetchFullCart(CartRequestAction.Bluecartmini)
              } else if (
                cartType === CartType.Active &&
                returnCartType &&
                returnCartType === CartType.MarketplaceCart
              ) {
                refetchFullCart(CartRequestAction.Marketplace)
              } else {
                refetchFullCart(getCartMiniAction(cartType))
              }
              closeMenu()
              return
            }
            if (!isOpen) return
            clearSelection()
            if (inputEl.current) inputEl.current.focus()
          },
        }
        return (
          <div className={classes.root}>
            <HeaderSearchInput
              id={`header-search-submit-${props.id}`}
              orgId={orgId}
              setFocusType={setFocusType}
              focusType={focusType}
              getInputProps={getInputProps}
              isOpen={isOpen}
              openMenu={openMenu}
              closeMenu={closeMenu}
              handleSearch={handleSearch}
              inputEl={inputEl}
              handleClearSelection={clearSelection}
              inputValue={inputValue}
              headerSearchAddToCartProps={_headerSearchAddToCartProps}
            />
            <HeaderSearchMenu
              orgId={orgId}
              focusType={focusType}
              isOpen={isOpen}
              minTermLength={minTermLength}
              getMenuProps={getMenuProps}
              getItemProps={getItemProps}
              suggestions={suggestions}
              suggestionsCount={suggestionsCount}
              recentSearches={recentSearches}
              recentPurchases={recentPurchases}
              highlightedIndex={highlightedIndex}
              searchTerm={searchTerm}
              getItemIndex={getItemIndex}
              prodNumberQuickLook={prodNumberQuickLook}
              setProdNumberQuickLook={setProdNumberQuickLook}
              headerSearchAddToCartProps={_headerSearchAddToCartProps}
              closeQuickLook={() => setShouldDisplayProductQuickLook(false)}
              closeMenu={closeMenu}
              toggleMarketplaceSellerModalOpen={
                toggleMarketplaceSellerModalOpen
              }
            />
            {marketplaceModalIsOpen && (
              <MarketplaceSellerModal
                open={marketplaceModalIsOpen}
                onCancel={toggleMarketplaceSellerModalOpen}
              />
            )}
          </div>
        )
      }}
    </Downshift>
  )
}

export default HeaderSearch
